<template>
    <div>
        <h1 class="mb-8 font-bold text-3xl">
            <inertia-link class="text-indigo-400 hover:text-indigo-600" :href="route('events.index')">Events
            </inertia-link>
            <span class="text-indigo-400 font-medium">/</span>
            <inertia-link class="text-indigo-400 hover:text-indigo-600" :href="route('events.show', [eventInfo.id])">
                {{ eventInfo.name }}
            </inertia-link>
            <span class="text-indigo-400 font-medium">/</span>
            <inertia-link class="text-indigo-400 hover:text-indigo-600"
                          :href="route('attendee-import.index', [eventInfo.id])">Import Attendees
            </inertia-link>
            <span class="text-indigo-400 font-medium">/</span>
            Batch #{{ batch.id }}
        </h1>

        <steps class="mb-6" current-step="deduplicate" :batch-id="batch.id"></steps>

        <info-box>
            Avoid creating duplicate records by associating import data with existing contacts, allowing information to be reused between events.
            If Contact ID, Attendee ID, or External ID are present in the imported data, this mapping will have occurred automatically and can be reviewed below.
            Otherwise, review the "Potential Duplicates" column and select the best match if a duplicate has been identified.
            Or manually select a contact using the search/select field in the "Map to Contact" column.
        </info-box>

        <table-wrapper :headers="['Given Name', 'Surname', 'Map to Contact', 'Potential Duplicates']" class="mb-6">
            <table-row v-for="record in records" :key="record.id">
                <table-cell>{{ record.given_name }}</table-cell>
                <table-cell>{{ record.surname }}</table-cell>
                <table-cell>
                    <contact-select v-model="selectedContacts[record.id]"></contact-select>
                </table-cell>
                <table-cell colspan="2">
                    <ul class="list-disc list-inside">
                        <li v-for="duplicate in record.duplicates" :key="duplicate.id" class="mb-2">
                            <span class="font-semibold">{{ duplicate.given_name }} {{ duplicate.surname }}</span>
                            <button
                                class="text-indigo-400 hover:text-indigo-600"
                                @click="selectContact(record.id, duplicate.id, duplicate.given_name, duplicate.surname, duplicate.unique_id)"
                            >
                                (Select this contact)
                            </button>
                            <ul class="list-disc list-inside pl-5">
                                <li><span class="font-semibold">Contact ID:</span>
                                    <a target="_blank" :href="route('contacts.edit', {contact: duplicate.unique_id})"
                                       class="text-indigo-400 hover:text-indigo-600">{{ duplicate.unique_id }}</a>
                                </li>
                                <li v-if="duplicate.primary_email"><span class="font-semibold">Primary Email:</span>
                                    {{ duplicate.primary_email }}
                                </li>
                                <li v-if="duplicate.mobile_phone"><span class="font-semibold">Mobile Phone:</span>
                                    {{ duplicate.mobile_phone }}
                                </li>
                                <li v-if="duplicate.work_phone"><span
                                    class="font-semibold">Work Phone: </span>{{ duplicate.work_phone }}
                                </li>
                                <li><span class="font-semibold">Last Updated:</span> {{
                                        dateFormat(duplicate.updated_at)
                                    }}
                                </li>
                            </ul>
                        </li>
                    </ul>
                </table-cell>
            </table-row>
        </table-wrapper>

        <div class="mb-6 flex items-center">
            <button class="btn-red" tabindex="-1" type="button" @click="destroy">Delete Batch</button>
            <button class="btn-indigo ml-auto" type="button" @click="saveSelectedContacts">
                Continue
            </button>
        </div>

    </div>
</template>

<script>
import Layout from "@/Shared/Layout"
import Icon from "@/Shared/Icon"
import TableWrapper from "@/Shared/Table/TableWrapper"
import TableRow from "@/Shared/Table/TableRow"
import TableCell from "@/Shared/Table/TableCell"
import TableCellEnd from "@/Shared/Table/TableCellEnd"
import SelectInput from "@/Shared/SelectInput"
import ContactSelect from "@/Shared/ContactSelect"
import Steps from "./ImportSteps.vue"
import {DateTime} from "luxon"
import {chain} from "lodash"
import InfoBox from "../../Shared/InfoBox.vue";

export default {
    metaInfo: {title: "Deduplicate"},
    components: {
        InfoBox,
        Icon,
        TableWrapper,
        TableRow,
        TableCell,
        TableCellEnd,
        SelectInput,
        ContactSelect,
        Steps,
    },
    layout: Layout,
    props: {
        batch: Object,
        eventInfo: Object,
        records: Array,
    },
    data() {
        return {
            selectedContacts: {},
        }
    },
    mounted() {
        this.selectedContacts = chain(this.records).keyBy('id').mapValues('contact').value();
    },
    watch: {},
    methods: {
        dateFormat(iso) {
            return DateTime.fromISO(iso).toFormat('dd LLL yyyy HH:mm ZZZZ')
        },
        selectContact(recordId, contactId, givenName, surname, uniqueId) {
            this.selectedContacts[recordId] = {
                "id": contactId,
                "unique_id": uniqueId,
                "surname": surname,
                "given_name": givenName,
            }
        },
        saveSelectedContacts() {
            this.$inertia.put(this.route('attendee-import-deduplicate.update', [this.batch.id]), {
                'contact_ids': chain(this.selectedContacts).mapValues('id').value(),
            });
        },
        destroy() {
            if (confirm('Are you sure you want to delete this attendee import batch?')) {
                this.$inertia.delete(this.route('attendee-import.destroy', [this.batch.id]))
            }
        },
    },
}
</script>
